/**
Project: Voice Panel (c)
Title: Edit Numbers 
Description: Component for editing the numbers and updates in the inventory.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Flex,
  Text,
  Dropdown,
  Dialog,
  RadioGroup,
  Button,
  EditIcon,
  Input,
  RadioGroupItem,
  Tooltip,
  FormLabel,
  AddIcon,
  ExclamationCircleIcon,
  Table,
  TableRow,
  TableCell,
  TrashCanIcon,
  Divider,
  Image,
} from "@fluentui/react-northstar";
import axios from "axios";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
  isNullOrEmpty,
  capitalizeFirstLetter,
  handleCommunicationPlatformName,
} from "../../common/actions/general-actions";
import AlertSuccessMessage from "../../common/helpers/alert-success-message";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import PageUtil from "../../../../core/helpers/page-util";
import IntlUtil from "../../../../core/helpers/intl-util";
import { Grid, Row, Col } from "react-flexbox-grid";
import { AppConfigProps } from "../../../../core/settings/app-config";
import PageLoader from "../../common/helpers/page-loader";
import { manageError } from "../../../../core/actions/common-actions";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import ResponseUtil from "../../../../core/helpers/response-util";
import {
  getInventoryNumberById,
  getCarriersList,
  getDepartmentsList,
  getInventoryNumbers,
  getTenantAccountsNumberSyncNow,
  setInventoryNumbersPageAllRecords,
  getNumberTypeList,
  updateNumber,
  getSubscriberAddress,
  getDeviceTypeList,
  deleteDevice,
  getDeviceById,
  getDeviceMakeModel,
  setDeviceMakeModel,
  addDevicesByNumberId,
  updateDevicesByNumberId,
  getCommunicationPlatformByName,
} from "../actions/number-inventory-actions";
import { DeviceTableHeaderColumns } from "../settings/numbers-table-header-columns";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import classNames from "classnames";
import NewSubscriberAddress from "./subscriber-address-add";
import { AppPageTitle } from "../../../../core/components/app-page-title";
import Texty from "react-texty";
import _ from "lodash";
import moment from "moment";

class NumberEdit extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_number_inventory = "vpa_number_inventory";

  constructor(props) {
    super(props);
    this.numberEditPageRef = React.createRef();
    this.state = {
      isEdited: false,
      isDisabled: true,
      newAddress: false,
      newDevice: false,
      isShowTooltip: false,
      isPageDataFetched: false,
      isAddNewDeviceDialog: false,
      isUpdateDeviceDialog: false,
      isDevicesDeleteDialog: false,
      updateDevice: false,
      deviceConnectedStatus: "",
      editNumberFields: this.editNumberFields(),
      deviceFormFields: this.deviceFormFields(),
      numberDataResult: null,
      departmentsList: [],
      numbersList: [],
      fullAddress: [],
      numberTypeList: [],
      addressList: [],
      deviceTypeList: [],
      deviceList: [],
      errorDetails: null,
      errorMessage: null,
      successMessage: null,
      errorDeviceMessage: null,
      errorDeviceDetails: null,
      successDeviceMessage: null,
      syncRecordsDetails: null,
      address: null,
      number: null,
      makeList: [],
      modelList: [],
      selectedDeviceId: null,
    };
  }
  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    const numberId = this.props.match.params.nid;
    await this.loadPageData(numberId);
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  async componentDidUpdate(prevProps, prevState, snapshot) {
    const numberId = this.props.match.params.nid;
    if (numberId !== prevProps.match.params.nid) {
      PageUtil.scrollToTop();
      await this.loadPageData(numberId);
    }
  }

  loadPageData = async (numberId) => {
    await this.setStateAsync({ isPageDataFetched: false });

    await this.fetchInventoryNumberById(numberId);
    await this.fetchEditNumbersData();
    if (this.props.inventoryDeviceMakeModelData === null) {
      await this.fetchDeviceMakeModel();
    }
    await this.setStateAsync({ isPageDataFetched: true });
  };

  fetchEditNumbersData = async () => {
    await axios
      .all([
        getInventoryNumbers(this._cancelToken),
        getDepartmentsList(this._cancelToken),
        getCarriersList(this._cancelToken),
        getNumberTypeList(this._cancelToken),
        getSubscriberAddress(this._cancelToken),
        getDeviceTypeList(this._cancelToken),
        getTenantAccountsNumberSyncNow(this._cancelToken),
      ])
      .then(
        axios.spread(
          async (
            inventoryNumbers,
            departments,
            carriers,
            numberTypes,
            address,
            deviceTypes,
            numberSync
          ) => {
            if (
              inventoryNumbers &&
              inventoryNumbers.data &&
              inventoryNumbers.status === AppConfigProps.httpStatusCode.ok &&
              departments &&
              departments.status === AppConfigProps.httpStatusCode.ok &&
              departments.data &&
              carriers &&
              carriers.status === AppConfigProps.httpStatusCode.ok &&
              carriers.data &&
              numberTypes &&
              numberTypes.status === AppConfigProps.httpStatusCode.ok &&
              numberTypes.data &&
              address &&
              address.status === AppConfigProps.httpStatusCode.ok &&
              address.data &&
              deviceTypes &&
              deviceTypes.status === AppConfigProps.httpStatusCode.ok &&
              deviceTypes.data &&
              numberSync &&
              numberSync.status === AppConfigProps.httpStatusCode.ok &&
              numberSync.data
            ) {
              if (inventoryNumbers.data.records) {
                await this.props.setInventoryNumbersPageAllRecords(
                  inventoryNumbers.data.records
                );
                let numbersSorted = _.sortBy(
                  inventoryNumbers.data.records,
                  (number) => {
                    return parseInt(number.phoneNumber);
                  }
                ).map((number) => {
                  return number.phoneNumber;
                });
                await this.setStateAsync({ numbersList: numbersSorted });
              }
              if (departments.data.records) {
                let departmentsList = [];
                departments.data.records.forEach((dept) => {
                  departmentsList.push({
                    key: dept.departmentId,
                    header: dept.departmentName,
                  });
                });
                departmentsList = [{ key: "", header: "" }].concat(
                  departmentsList
                );
                let departmentSortedList = _.orderBy(
                  departmentsList,
                  [(department) => department.header?.toLowerCase()],
                  ["asc"]
                );
                await this.setStateAsync({
                  departmentsList: departmentSortedList,
                });
              }
              if (carriers.data.records) {
                let carriersList = [];
                carriers.data.records.forEach(async (carrier) => {
                  carriersList.push({
                    key: carrier.carrierId,
                    header: carrier.carrierName,
                  });
                });
                carriersList = [{ key: "", header: "" }].concat(carriersList);
                let carrierSortedList = _.orderBy(
                  carriersList,
                  [(carrier) => carrier.header?.toLowerCase()],
                  ["asc"]
                );
                await this.setStateAsync({
                  carriersList: carrierSortedList,
                });
              }
              if (numberTypes.data.records) {
                let numberTypeList = [];
                numberTypes.data.records.forEach(async (numType) => {
                  numberTypeList.push({
                    key: numType.numberTypeId,
                    header: numType.numberTypeName,
                  });
                });
                numberTypeList = [{ key: "", header: "" }].concat(
                  numberTypeList
                );
                let numberTypeSortedList = _.orderBy(
                  numberTypeList,
                  [(numberType) => numberType.header?.toLowerCase()],
                  ["asc"]
                );
                await this.setStateAsync({
                  numberTypeList: numberTypeSortedList,
                });
              }
              if (address.data.result) {
                let fullAddress = [];
                address.data.result.addresses.forEach(async (address) => {
                  fullAddress.push({
                    key: address.fullAddress,
                    header: address.fullAddress,
                  });
                });
                fullAddress.unshift({ key: "", header: "None" });
                fullAddress = _.uniqBy(fullAddress, (address) =>
                  address.key.toLowerCase()
                );
                await this.setStateAsync({
                  fullAddress: fullAddress,
                  addressList: address.data.result.addresses,
                });
              }
              if (deviceTypes.data.records) {
                let deviceTypeList = [];
                deviceTypes.data.records.forEach(async (devType) => {
                  deviceTypeList.push({
                    key: devType.deviceTypeId,
                    header: devType.deviceTypeName,
                  });
                });
                deviceTypeList = [{ key: "", header: "" }].concat(
                  deviceTypeList
                );
                let deviceTypeSortedList = _.orderBy(
                  deviceTypeList,
                  [(device) => device.header?.toLowerCase()],
                  ["asc"]
                );
                await this.setStateAsync({
                  deviceTypeList: deviceTypeSortedList,
                  deviceTypeRecord: deviceTypes.data.records,
                });
              }
              if (numberSync.data.result) {
                await this.setStateAsync({
                  syncRecordsDetails: numberSync.data.result,
                });
                if (
                  this.state.editNumberFields.manageStatus !== null &&
                  this.state.editNumberFields.manageStatus
                    .toLowerCase()
                    .trim() ===
                    VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED.toLowerCase() &&
                  this.state.editNumberFields.communicationPlatform ===
                    "Microsoft Teams"
                ) {
                  await this.setStateAsync({
                    isShowTooltip: true,
                  });
                  await setTimeout(
                    () => this.setStateAsync({ isShowTooltip: false }),
                    5000
                  );
                }
              }
            } else {
              await manageError(new Error(), this.props.history);
            }
          }
        )
      )
      .catch(async (err) => {
        await manageError(err, this.props.history);
      });
  };

  fetchDeviceMakeModel = async () => {
    await getDeviceMakeModel(this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data &&
          res.data.records
        ) {
          this.props.setDeviceMakeModel(res.data.records);
        } else {
          await manageError(res, this.props.history);
        }
      })
      .catch(async (err) => {
        await manageError(err, this.props.history);
      });
  };

  handleNumberEdit = async () => {
    await this.setStateAsync({ isEdited: true });
  };

  handleBack = () => {
    this.props.history.push(VoiceAdminURLProps.numberList);
  };

  editNumberFields = () => {
    return {
      phoneNumber: "",
      userName: "",
      departmentId: "",
      departmentName: "",
      numberTypeId: "",
      numberTypeName: "",
      carrierId: "",
      carrierName: "",
      deviceTypeId: "",
      deviceTypeName: "",
      deviceSerialNumber: "",
      streetAddress1: "",
      streetAddress2: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
      numberNotes: "",
      manageStatus: "",
      assignStatus: "",
      reserveStatus: "",
      address: "",
    };
  };

  deviceFormFields = () => {
    return {
      deviceTypeId: "",
      deviceTypeName: "",
      makeName: "",
      modelName: "",
      serialNumber: "",
    };
  };

  fetchInventoryNumberById = async (numberId) => {
    await getInventoryNumberById(numberId, this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.data &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data.result
        ) {
          await this.setFormInputFields(res.data.result);
        } else {
          await manageError(res, this.props.history);
        }
      })
      .catch(async (err) => {
        manageError(err, this.props.history);
      });
  };

  setFormInputFields = async (numberRecord) => {
    if (numberRecord) {
      const data = {
        ...numberRecord,
        manageStatus:
          numberRecord.manageStatus?.toLowerCase().trim() ===
          VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED.toLowerCase()
            ? VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED
            : VoiceAdminConstants.INVENTORY.MANAGE_STATUS.UNMANAGED,
        reserveStatus:
          numberRecord.reserveStatus?.toLowerCase().trim() ===
          VoiceAdminConstants.INVENTORY.RESERVED_STATUS.RESERVED.toLowerCase()
            ? VoiceAdminConstants.INVENTORY.RESERVED_STATUS.RESERVED
            : VoiceAdminConstants.INVENTORY.RESERVED_STATUS.UNRESERVED,
        assignStatus:
          numberRecord.assignStatus?.toLowerCase().trim() ===
          VoiceAdminConstants.INVENTORY.ASSIGN_STATUS.ASSIGNED.toLowerCase()
            ? VoiceAdminConstants.INVENTORY.ASSIGN_STATUS.ASSIGNED
            : VoiceAdminConstants.INVENTORY.ASSIGN_STATUS.UNASSIGNED,
        address: "",
      };
      await this.setStateAsync({
        numberDataResult: numberRecord,
        editNumberFields: data,
        isEdited: false,
      });
      if (numberRecord && numberRecord.devices) {
        if (numberRecord.devices.length > 0) {
          this.setStateAsync({ deviceList: numberRecord.devices });
          await this.setStateAsync({
            deviceConnectedStatus:
              VoiceAdminConstants.INVENTORY.ADD_DEVICE_STATUS.YES,
          });
        } else {
          await this.setStateAsync({
            deviceConnectedStatus:
              VoiceAdminConstants.INVENTORY.ADD_DEVICE_STATUS.NO,
            deviceList: [],
          });
        }
      }
      if (
        this.state.editNumberFields.manageStatus !== null &&
        this.state.editNumberFields.manageStatus.toLowerCase().trim() ===
          VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED.toLowerCase()
      ) {
        if (
          this.state.editNumberFields.assignStatus !== null &&
          this.state.editNumberFields.assignStatus.toLowerCase().trim() ===
            VoiceAdminConstants.INVENTORY.ASSIGN_STATUS.ASSIGNED.toLowerCase()
        ) {
          await this.setStateAsync({
            isDisabled: true,
          });
        } else {
          await this.setStateAsync({
            isDisabled: false,
          });
        }
      } else {
        await this.setStateAsync({
          isDisabled: false,
        });
      }
    }
  };

  setEditFormFieldValue = async (fieldName, fieldValue) => {
    if (fieldName && this.state.editNumberFields) {
      await this.setStateAsync({
        editNumberFields: {
          ...this.state.editNumberFields,
          [fieldName]: fieldValue,
        },
      });
      if (fieldName === "departmentId") {
        await this.setStateAsync({
          editNumberFields: {
            ...this.state.editNumberFields,
            [fieldName]: fieldValue?.key,
            departmentName: fieldValue?.header,
          },
        });
      }
      if (fieldName === "numberTypeId") {
        await this.setStateAsync({
          editNumberFields: {
            ...this.state.editNumberFields,
            [fieldName]: fieldValue?.key,
            numberTypeName: fieldValue?.header,
          },
        });
      }
      if (fieldName === "carrierId") {
        await this.setStateAsync({
          editNumberFields: {
            ...this.state.editNumberFields,
            [fieldName]: fieldValue?.key,
            carrierName: fieldValue?.header,
          },
        });
      }
      if (fieldName === "connectedToDevice") {
        await this.setStateAsync({
          deviceConnectedStatus: fieldValue,
        });
      }
      if (fieldName === "address") {
        let currentAddress = this.state.addressList.find((newAddress) => {
          if (newAddress.fullAddress === fieldValue?.header) {
            return newAddress;
          } else {
            return false;
          }
        });
        await this.setStateAsync({
          editNumberFields: {
            ...this.state.editNumberFields,
            [fieldName]: fieldValue.header,
            streetAddress1: currentAddress?.addLine1
              ? currentAddress.addLine1
              : null,
            streetAddress2: currentAddress?.addLine2
              ? currentAddress.addLine2
              : null,
            city: currentAddress?.city ? currentAddress.city : null,
            state: currentAddress?.state ? currentAddress.state : null,
            country: currentAddress?.country ? currentAddress.country : null,
            postalCode: currentAddress?.zip ? currentAddress.zip : null,
          },
        });
      }
    }
    await this.clearAlert();
  };

  handleEditNumberPhoneFormFieldChange = async (e, select) => {
    let numberData = this.props.inventoryNumbersAllRecords.find(
      (number) => number.phoneNumber === select.value
    );
    this.props.history.push(
      VoiceAdminURLProps.numberEdit.replace(
        ":nid",
        numberData?.numberId ? numberData.numberId : this.props.match.params.nid
      )
    );
    await this.clearAlert();
  };

  getAssignStatus() {
    return [
      {
        key: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.addStatus.assigned"
        ),
        label: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.addStatus.assigned"
        ),
        value: VoiceAdminConstants.INVENTORY.ASSIGN_STATUS.ASSIGNED,
      },
      {
        key: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.addStatus.unassigned"
        ),
        label: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.addStatus.unassigned"
        ),
        value: VoiceAdminConstants.INVENTORY.ASSIGN_STATUS.UNASSIGNED,
      },
    ];
  }

  getReserveStatus() {
    return [
      {
        key: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.addReserve.yes"
        ),
        label: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.addReserve.yes"
        ),
        value: VoiceAdminConstants.INVENTORY.RESERVED_STATUS.RESERVED,
        disabled: this.state.isDisabled,
      },
      {
        key: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.addReserve.no"
        ),
        label: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.addReserve.no"
        ),
        value: VoiceAdminConstants.INVENTORY.RESERVED_STATUS.UNRESERVED,
        disabled: this.state.isDisabled,
      },
    ];
  }

  getAddDeviceStatus() {
    return [
      {
        key: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.yes"
        ),
        label: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.yes"
        ),
        value: VoiceAdminConstants.INVENTORY.ADD_DEVICE_STATUS.YES,
        disabled: this.state.deviceList.length > 0,
      },
      {
        key: IntlUtil.getText(this._intl_ns_vpa_number_inventory, "content.no"),
        label: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "content.no"
        ),
        value: VoiceAdminConstants.INVENTORY.ADD_DEVICE_STATUS.NO,
        disabled: this.state.deviceList.length > 0,
      },
    ];
  }

  clearAlert = async () => {
    await this.setStateAsync({
      successMessage: null,
      errorMessage: null,
      errorDetails: null,
      errorDeviceMessage: null,
      errorDeviceDetails: null,
      successDeviceMessage: null,
    });
  };

  fetchSubscriberAddress = async () => {
    await getSubscriberAddress(this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.data &&
          res.data.result &&
          res.status === AppConfigProps.httpStatusCode.ok
        ) {
          this.props.hidePageWaitDialog();
          if (res.data.result.addresses.length > 0) {
            let fullAddress = [];
            res.data.result.addresses.forEach(async (address) => {
              fullAddress.push({
                key: address.fullAddress,
                header: address.fullAddress,
              });
            });
            fullAddress.unshift({ key: "", header: "None" });
            fullAddress = _.uniqBy(fullAddress, (address) =>
              address.key.toLowerCase()
            );
            await this.setStateAsync({
              fullAddress: fullAddress,
              addressList: res.data.result.addresses,
            });
          } else {
            let fullAddress = [{ key: "", header: "None" }];
            await this.setStateAsync({
              fullAddress: fullAddress,
            });
          }
        }
      })
      .catch(async (err) => {
        this.props.hidePageWaitDialog();
        const errorStatus = ResponseUtil.getStatus(err);
        let errMessage = IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.error.errorResponse"
        );
        if (
          errorStatus &&
          errorStatus === AppConfigProps.httpStatusCode.badRequest
        ) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
              VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
          ) {
            this.props.history.push(VoiceAdminURLProps.setup);
            return;
          } else if (
            primeError &&
            primeError.code ===
              VoiceAdminConstants.SERVER_ERROR_CODE.ATL_API_FAILED
          ) {
            await this.setStateAsync({
              errorDetails: primeError.message,
            });
          }
          await this.setStateAsync({
            errorMessage: errMessage,
            showUsAlert: true,
          });
        } else {
          await manageError(err, this.props.history);
        }
      });
  };

  handleDeleteDevice = async (DeviceId) => {
    await this.clearAlert();
    await this.setStateAsync({ isDevicesDeleteModal: false });
    if (this.props.match.params.nid && DeviceId) {
      await this.props.showPageWaitDialog(
        IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.info.pleaseWaiDeletingNumberData"
        )
      );
      await deleteDevice(
        this.props.match.params.nid,
        DeviceId,
        this._cancelToken
      )
        .then(async (res) => {
          if (res && res.status === AppConfigProps.httpStatusCode.ok) {
            await this.props.hidePageWaitDialog();
            await this.setStateAsync({
              successMessage: IntlUtil.getText(
                this._intl_ns_vpa_number_inventory,
                "notification.success.deletedDevice"
              ),
              showAlert: true,
            });
            if (this.state.deviceList && this.state.deviceList.length > 0) {
              let result = this.state.deviceList.filter((device) => {
                return device.deviceId !== DeviceId;
              });
              await this.setStateAsync({ deviceList: result });
            }
            PageUtil.scrollIntoView(this.numberEditPageRef);
          } else {
            await this.props.hidePageWaitDialog();
            await manageError(res, this.props.history);
          }
        })
        .catch(async (err) => {
          await this.props.hidePageWaitDialog();
          const errorStatus = ResponseUtil.getStatus(err);
          if (
            errorStatus &&
            errorStatus === AppConfigProps.httpStatusCode.badRequest
          ) {
            await this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_number_inventory,
                "notification.error.deleteNumberFailed"
              ),
              showAlert: true,
            });
          } else {
            await manageError(err, this.props.history);
          }
        });
    }
  };

  fetchDeviceById = (deviceId) => {
    if (this.props.match.params.nid && deviceId) {
      getDeviceById(this.props.match.params.nid, deviceId, this._cancelToken)
        .then(async (res) => {
          if (
            res &&
            res.data &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data.result
          ) {
            await this.setStateAsync({
              editDeviceValue: res.data.result,
            });
          } else {
            await manageError(res, this.props.history);
          }
        })
        .catch(async (err) => {
          manageError(err, this.props.history);
        });
    }
  };

  handleUpdateNumberInventory = async () => {
    await this.clearAlert();
    PageUtil.scrollIntoView(this.numberEditPageRef);
    if (
      this.state.editNumberFields &&
      this.state.editNumberFields.phoneNumber?.trim() !== "" &&
      this.state.editNumberFields.assignStatus?.trim() !== "" &&
      this.state.editNumberFields.reserveStatus?.trim() !== "" &&
      this.state.editNumberFields.manageStatus?.trim() !== ""
    ) {
      if (
        this.state.editNumberFields &&
        this.props.match.params &&
        this.props.match.params.nid
      ) {
        await this.props.showPageWaitDialog(
          IntlUtil.getText(
            this._intl_ns_vpa_number_inventory,
            "notification.info.pleaseWaitUpdatingData"
          )
        );
        let numbersObj = {
          ...this.state.editNumberFields,
          departmentId: this.state.editNumberFields?.departmentId
            ? this.state.editNumberFields.departmentId.trim()
            : null,
          numberTypeId: this.state.editNumberFields?.numberTypeId
            ? this.state.editNumberFields.numberTypeId.trim()
            : null,
          numberNotes: this.state.editNumberFields?.numberNotes
            ? this.state.editNumberFields.numberNotes.trim()
            : null,
          deviceTypeId: this.state.editNumberFields?.deviceTypeId
            ? this.state.editNumberFields?.deviceTypeId?.trim()
            : null,
          deviceSerialNumber: this.state.editNumberFields?.deviceSerialNumber
            ? this.state.editNumberFields.deviceSerialNumber?.trim()
            : null,
          classification: this.state.editNumberFields?.classification,
        };
        if (
          this.state.editNumberFields.manageStatus?.toLowerCase().trim() ===
          VoiceAdminConstants.INVENTORY.MANAGE_STATUS.UNMANAGED.toLowerCase()
        ) {
          numbersObj = {
            ...numbersObj,
            userName: this.state.editNumberFields?.userName
              ? this.state.editNumberFields.userName.trim()
              : null,
            assignStatus: this.state.editNumberFields?.assignStatus
              ? this.state.editNumberFields.assignStatus.trim()
              : null,
            reserveStatus: this.state.editNumberFields?.reserveStatus
              ? this.state.editNumberFields.reserveStatus.trim()
              : null,
            carrierId: this.state.editNumberFields?.carrierId
              ? this.state.editNumberFields.carrierId.trim()
              : null,
            streetAddress1: this.state.editNumberFields?.streetAddress1
              ? this.state.editNumberFields.streetAddress1.trim()
              : null,
            streetAddress2: this.state.editNumberFields?.streetAddress2
              ? this.state.editNumberFields.streetAddress2.trim()
              : null,
            city: this.state.editNumberFields?.city
              ? this.state.editNumberFields.city.trim()
              : null,
            state: this.state.editNumberFields?.state
              ? this.state.editNumberFields.state.trim()
              : null,
            country: this.state.editNumberFields?.country
              ? this.state.editNumberFields.country.trim()
              : null,
            postalCode: this.state.editNumberFields?.postalCode
              ? this.state.editNumberFields.postalCode.trim()
              : null,
          };
        }
        numbersObj = _.omit(numbersObj, "devices");
        await updateNumber(
          numbersObj,
          this.props.match.params.nid,
          this._cancelToken
        )
          .then(async (res) => {
            if (
              res &&
              res.status === AppConfigProps.httpStatusCode.ok &&
              res.data &&
              res.data.result
            ) {
              await this.fetchInventoryNumberById(this.props.match.params.nid);
              await this.props.hidePageWaitDialog();
              await this.setStateAsync({
                successMessage: IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "notification.success.updateNumber"
                ),
                errorMessage: null,
              });
            } else {
              await this.props.hidePageWaitDialog();
              await this.setStateAsync({
                errorMessage: IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "notification.error.updateNumberFailed"
                ),
              });
            }
          })
          .catch(async (err) => {
            await this.props.hidePageWaitDialog();
            const errorStatus = ResponseUtil.getStatus(err);
            if (
              errorStatus &&
              errorStatus === AppConfigProps.httpStatusCode.badRequest
            ) {
              const primeError = ResponseUtil.getPrimeError(err);
              if (
                primeError &&
                (primeError.code ===
                  VoiceAdminConstants.SERVER_ERROR_CODE
                    .PHONE_NUMBER_ALREADY_EXISTS ||
                  primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE
                      .UPDATE_NUMBER_FAILED ||
                  primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE.NUMBERTYPE_INVALID ||
                  primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE.DEPARTMENT_INVALID ||
                  primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE.CARRIER_INVALID)
              ) {
                await this.setStateAsync({
                  errorDetails: primeError.message,
                });
              }
              await this.setStateAsync({
                errorMessage: IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "notification.error.updateNumberFailed"
                ),
                successMessage: null,
                importData: null,
              });
            } else {
              await manageError(err, this.props.history);
            }
          });
      }
    } else if (this.state.editNumberFields.phoneNumber.trim() === "") {
      this.setStateAsync({
        errorMessage: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.error.phoneNumberRequired"
        ),
        successMessage: null,
      });
    } else if (this.state.editNumberFields.assignStatus.trim() === "") {
      this.setStateAsync({
        errorMessage: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.error.assignedStatusRequired"
        ),
        successMessage: null,
      });
    } else if (this.state.editNumberFields.reserveStatus.trim() === "") {
      this.setStateAsync({
        errorMessage: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.error.reservedStatusRequired"
        ),
        successMessage: null,
      });
    } else if (this.state.editNumberFields.manageStatus.trim() === "") {
      this.setStateAsync({
        errorMessage: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.error.managedStatusRequired"
        ),
        successMessage: null,
      });
    }
  };

  handleHideDeleteDialog = async () => {
    await this.setState({ isDevicesDeleteModal: false });
  };

  handleShowDeleteDialog = async (number) => {
    if (number) {
      await this.clearAlert();
      await this.setState({ numberSelect: number });
      await this.setState({ isDevicesDeleteModal: true });
    }
  };

  renderDeviceDeleteDialog = () => {
    if (this.state.numberSelect) {
      return (
        <Dialog
          backdrop={true}
          cancelButton={IntlUtil.getText(
            this._intl_ns_vpa_number_inventory,
            "content.no"
          )}
          confirmButton={IntlUtil.getText(
            this._intl_ns_vpa_number_inventory,
            "content.yes"
          )}
          onConfirm={() => {
            this.handleDeleteDevice(this.state.numberSelect.deviceId);
          }}
          onCancel={() => {
            this.handleHideDeleteDialog();
          }}
          open={this.state.isDevicesDeleteModal}
          content={
            <Grid fluid className="p-l-0 p-r-0">
              <Row className="m-t-10 m-b-10 m-l-0 p-l-0">
                <Col xs={12} md={12} lg={12} xl={12}>
                  <Text className="vpa-page-dialog-info-text">
                    {IntlUtil.getText(
                      this._intl_ns_vpa_number_inventory,
                      "notification.info.deletePermanentlyTextPrefixDevice"
                    )}
                    <b className="m-l-5">
                      {this.state.numberSelect.deviceTypeName}
                    </b>
                    {IntlUtil.getText(
                      this._intl_ns_vpa_number_inventory,
                      "notification.info.deletePermanentlyTextSuffix"
                    )}
                  </Text>
                </Col>
                <Col xs={12} md={12} lg={12} xl={12}>
                  <Text
                    className="vpa-page-dialog-info-text"
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_number_inventory,
                      "notification.info.deletePermanentlyText"
                    )}
                  />
                </Col>
              </Row>
            </Grid>
          }
          className="vpa-page-dialog"
        />
      );
    }
  };

  handleShowDeviceDialog = async (Id) => {
    await this.clearAlert();
    await this.setStateAsync({ selectedDeviceId: Id });
    await this.state.deviceList.forEach(async (item) => {
      if (item.deviceId === Id) {
        let makeList = [];
        if (
          this.props.inventoryDeviceMakeModelData &&
          item.deviceTypeName !== ""
        ) {
          this.props.inventoryDeviceMakeModelData.forEach(
            async (deviceTypeData) => {
              if (deviceTypeData.deviceTypeName === item.deviceTypeName) {
                makeList.push({
                  key: deviceTypeData.makeName,
                  header: deviceTypeData.makeName,
                });
              }
            }
          );
          makeList = [{ key: "", header: "" }].concat(makeList);
          makeList = _.orderBy(
            makeList,
            [(makeName) => makeName.header.toLowerCase()],
            ["asc"]
          );
          makeList = _.uniqBy(makeList, "header");
          await this.setStateAsync({
            makeList: makeList,
          });
          await this.setStateAsync({
            deviceFormFields: {
              ...this.state.deviceFormFields,
              makeName: "",
              modelName: "",
            },
          });
        }
        let modelList = [];
        if (
          this.props.inventoryDeviceMakeModelData &&
          item.deviceTypeName !== "" &&
          item.makeName !== ""
        ) {
          this.props.inventoryDeviceMakeModelData.forEach(
            async (deviceTypeData) => {
              if (
                deviceTypeData.deviceTypeName === item.deviceTypeName &&
                deviceTypeData.makeName === item.makeName
              ) {
                modelList.push({
                  key: deviceTypeData.modelName,
                  header: deviceTypeData.modelName,
                });
              }
            }
          );
          modelList = [{ key: "", header: "" }].concat(modelList);
          modelList = _.orderBy(
            modelList,
            [(modelName) => modelName.header.toLowerCase()],
            ["asc"]
          );
          modelList = _.uniqBy(modelList, "header");
          await this.setStateAsync({
            modelList: modelList,
          });
          await this.setStateAsync({
            deviceFormFields: {
              ...this.state.deviceFormFields,
              modelName: "",
            },
          });
        }
        await this.setStateAsync({
          deviceFormFields: {
            ...this.state.deviceFormFields,
            deviceTypeId: item.deviceTypeId,
            deviceTypeName: item.deviceTypeName,
            serialNumber: item.serialNumber,
            makeName: item.makeName,
            modelName: item.modelName,
          },
        });
      }
    });
    await this.setState({ isUpdateDeviceDialog: true });
  };

  renderDeviceEdit = () => {
    return (
      <Dialog
        className="vpa-bn-dialog"
        open={this.state.isUpdateDeviceDialog}
        content={
          <>
            <Text className="page-text text-semibold vpa-dialog-header text-fc-primary">
              {IntlUtil.getText(
                this._intl_ns_vpa_number_inventory,
                "content.editDevice"
              )}
            </Text>
            <Divider size={1} />
            <Grid fluid className="page-grid m-l-0 p-l-0">
              <Row className="m-t-5 m-l-0 p-l-0">
                {this.state.errorDeviceMessage &&
                this.state.errorDeviceMessage.length > 0 ? (
                  <Col>
                    <AlertErrorMessage
                      content={this.state.errorDeviceMessage}
                      moreDetails={{
                        label: IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.moreDetails"
                        ),
                        content: this.state.errorDeviceDetails,
                      }}
                    />
                  </Col>
                ) : null}
                {this.state.successDeviceMessage &&
                this.state.successDeviceMessage.length > 0 ? (
                  <Col>
                    <AlertSuccessMessage
                      content={this.state.successDeviceMessage}
                    />
                  </Col>
                ) : null}
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={6} lg={6} xl={6} className="vpa-bn-text">
                  <div>
                    <FormLabel
                      required
                      className="page-text text-semibold text-fc-dark"
                    >
                      {IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.device_type"
                      )}
                    </FormLabel>
                  </div>
                  <div className="m-t-20">
                    <Dropdown
                      highlightFirstItemOnOpen={isNullOrEmpty(
                        this.state.deviceFormFields.deviceTypeName
                      )}
                      name="deviceTypeName"
                      placeholder="Select Device Type"
                      items={this.state.deviceTypeList}
                      className="vpa-dropdown"
                      renderItem={(Item, props) => {
                        if (props.key === "") {
                          return (
                            <Item
                              {...props}
                              className="vpa-dropdown-none"
                              header={props.header}
                            />
                          );
                        } else {
                          return <Item {...props} header={props.header} />;
                        }
                      }}
                      inverted
                      defaultValue={this.state.deviceFormFields.deviceTypeName}
                      value={this.state.deviceFormFields.deviceTypeName}
                      onChange={(e, select) => {
                        if (
                          this.state.deviceFormFields.deviceTypeName !==
                          select?.value?.header
                        ) {
                          this.setDeviceFormFieldValue(
                            select.name,
                            select.value.header
                          );
                        }
                      }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6} lg={6} xl={6} className="vpa-bn-text">
                  <div>
                    <FormLabel className="page-text text-semibold text-fc-dark">
                      {IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.make_Brand"
                      )}
                    </FormLabel>
                  </div>
                  <div className="m-t-20">
                    <Dropdown
                      highlightFirstItemOnOpen={isNullOrEmpty(
                        this.state.deviceFormFields.makeName
                      )}
                      items={this.state.makeList}
                      id="makeName"
                      name="makeName"
                      placeholder="Select Make"
                      className="vpa-dropdown"
                      renderItem={(Item, props) => {
                        if (props.key === "") {
                          return (
                            <Item
                              {...props}
                              className="vpa-dropdown-none"
                              header={props.header}
                            />
                          );
                        } else {
                          return <Item {...props} header={props.header} />;
                        }
                      }}
                      inverted={true}
                      value={this.state.deviceFormFields.makeName}
                      defaultValue={this.state.deviceFormFields.makeName}
                      onChange={(e, select) => {
                        if (
                          this.state.deviceFormFields.makeName !==
                          select?.value?.header
                        ) {
                          this.setDeviceFormFieldValue(
                            select.name,
                            select.value.key
                          );
                        }
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={6} lg={6} xl={6} className="vpa-bn-text">
                  <div>
                    <FormLabel className="page-text text-semibold text-fc-dark">
                      {IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.model"
                      )}
                    </FormLabel>
                  </div>
                  <div className="m-t-20">
                    <Dropdown
                      highlightFirstItemOnOpen={isNullOrEmpty(
                        this.state.deviceFormFields.modelName
                      )}
                      items={this.state.modelList}
                      id="modelName"
                      name="modelName"
                      placeholder="Select Model"
                      className="vpa-dropdown"
                      renderItem={(Item, props) => {
                        if (props.key === "") {
                          return (
                            <Item
                              {...props}
                              className="vpa-dropdown-none"
                              header={props.header}
                            />
                          );
                        } else {
                          return <Item {...props} header={props.header} />;
                        }
                      }}
                      inverted={true}
                      value={this.state.deviceFormFields.modelName}
                      defaultValue={this.state.deviceFormFields.modelName}
                      onChange={(e, select) => {
                        if (
                          this.state.deviceFormFields.modelName !==
                          select?.value?.header
                        ) {
                          this.setDeviceFormFieldValue(
                            select.name,
                            select.value.key
                          );
                        }
                      }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6} lg={6} xl={6} className="vpa-bn-text">
                  <div>
                    <FormLabel className="page-text text-semibold text-fc-dark">
                      {IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.serialNumber"
                      )}
                    </FormLabel>
                  </div>
                  <div className="m-t-20">
                    <Input
                      id="serialNumber"
                      name="serialNumber"
                      className="vpa-inventory-input"
                      inverted={true}
                      value={this.state.deviceFormFields.serialNumber}
                      defaultValue={this.state.deviceFormFields.serialNumber}
                      onChange={(e) => {
                        this.setDeviceFormFieldValue(
                          e.target.id,
                          e.target.value
                        );
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="m-t-20 m-l-0">
                <Col className="m-r-10">
                  <Button
                    onClick={() => this.editDevices()}
                    primary
                    className="vpa-save-btn"
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_number_inventory,
                      "content.save"
                    )}
                  />
                </Col>
                <Col className="m-r-10">
                  <Button
                    onClick={() =>
                      this.setState({ isUpdateDeviceDialog: false })
                    }
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_number_inventory,
                      "content.close"
                    )}
                  />
                </Col>
              </Row>
            </Grid>
          </>
        }
      />
    );
  };

  addDeviceData = async () => {
    await this.clearAlert();
    if (this.state.deviceFormFields.deviceTypeName !== "") {
      let duplicates = [];
      this.state.deviceList.forEach((device) => {
        if (
          device.deviceTypeName.trim().toLowerCase() ===
            this.state.deviceFormFields.deviceTypeName.trim().toLowerCase() &&
          device.makeName.trim().toLowerCase() ===
            this.state.deviceFormFields.makeName.trim().toLowerCase() &&
          device.modelName.trim().toLowerCase() ===
            this.state.deviceFormFields.modelName.trim().toLowerCase() &&
          device.serialNumber.trim().toLowerCase() ===
            this.state.deviceFormFields.serialNumber.trim().toLowerCase()
        ) {
          duplicates.push(device);
        }
      });
      if (duplicates && duplicates.length > 0) {
        await this.setStateAsync({
          errorDeviceMessage: IntlUtil.getText(
            this._intl_ns_vpa_number_inventory,
            "notification.error.deviceDetails"
          ),
        });
      } else {
        await this.props.showPageWaitDialog(
          IntlUtil.getText(
            this._intl_ns_vpa_number_inventory,
            "notification.info.pleaseWaitAddingData"
          )
        );
        let deviceData = {
          deviceTypeId: this.state.deviceFormFields.deviceTypeId
            ? this.state.deviceFormFields.deviceTypeId.trim()
            : null,
          deviceTypeName: this.state.deviceFormFields.deviceTypeName
            ? this.state.deviceFormFields.deviceTypeName.trim()
            : null,
          makeName: this.state.deviceFormFields.makeName
            ? this.state.deviceFormFields.makeName.trim()
            : null,
          modelName: this.state.deviceFormFields?.modelName
            ? this.state.deviceFormFields.modelName.trim()
            : null,
          serialNumber: this.state.deviceFormFields?.serialNumber
            ? this.state.deviceFormFields.serialNumber.trim()
            : null,
        };
        await addDevicesByNumberId(
          this.props.match.params.nid,
          deviceData,
          this._cancelToken
        )
          .then(async (res) => {
            if (
              res &&
              res.data &&
              res.status === AppConfigProps.httpStatusCode.ok &&
              res.data.result
            ) {
              await this.setStateAsync({
                deviceList: [...this.state.deviceList, res.data.result],
              });
              await this.props.hidePageWaitDialog();
              await this.setStateAsync({
                successDeviceMessage: IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "notification.success.addDevice"
                ),
                deviceFormFields: this.deviceFormFields(),
              });
            } else {
              await this.props.hidePageWaitDialog();
              await this.setStateAsync({
                successDeviceMessage: IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "notification.error.addDeviceFailed"
                ),
              });
            }
          })
          .catch(async (err) => {
            await this.props.hidePageWaitDialog();
            const errorStatus = ResponseUtil.getStatus(err);
            if (
              errorStatus &&
              errorStatus === AppConfigProps.httpStatusCode.badRequest
            ) {
              const primeError = ResponseUtil.getPrimeError(err);
              if (
                primeError &&
                (primeError.code ===
                  VoiceAdminConstants.SERVER_ERROR_CODE.DEVICE_TYPE_NAME ||
                  primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE.DEVICE_ADD_FAILED ||
                  primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE.DEVICE_TYPE_REQUIRED)
              ) {
                await this.setStateAsync({
                  errorDeviceDetails: primeError.message,
                });
              }
              await this.setStateAsync({
                errorDeviceMessage: IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "notification.error.addDeviceFailed"
                ),
              });
            } else {
              await manageError(err, this.props.history);
            }
          });
      }
    } else {
      this.setStateAsync({
        errorDeviceMessage: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.error.deviceNameRequired"
        ),
      });
    }
  };

  editDevices = async () => {
    await this.clearAlert();
    if (this.state.deviceFormFields.deviceTypeName !== "") {
      let duplicates = [];
      this.state.deviceList.forEach((device) => {
        if (
          device.deviceId !== this.state.selectedDeviceId &&
          device.deviceTypeName.trim().toLowerCase() ===
            this.state.deviceFormFields.deviceTypeName.trim().toLowerCase() &&
          device.makeName.trim().toLowerCase() ===
            this.state.deviceFormFields.makeName.trim().toLowerCase() &&
          device.modelName.trim().toLowerCase() ===
            this.state.deviceFormFields.modelName.trim().toLowerCase() &&
          device.serialNumber.trim().toLowerCase() ===
            this.state.deviceFormFields.serialNumber.trim().toLowerCase()
        ) {
          duplicates.push(device);
        }
      });
      if (duplicates && duplicates.length > 0) {
        await this.setStateAsync({
          errorDeviceMessage: IntlUtil.getText(
            this._intl_ns_vpa_number_inventory,
            "notification.error.deviceDetails"
          ),
        });
      } else {
        await this.props.showPageWaitDialog(
          IntlUtil.getText(
            this._intl_ns_vpa_number_inventory,
            "notification.info.pleaseWaitUpdatingData"
          )
        );
        let deviceData = {
          deviceTypeId: this.state.deviceFormFields.deviceTypeId
            ? this.state.deviceFormFields.deviceTypeId.trim()
            : null,
          deviceTypeName: this.state.deviceFormFields.deviceTypeName
            ? this.state.deviceFormFields.deviceTypeName.trim()
            : null,
          makeName: this.state.deviceFormFields.makeName
            ? this.state.deviceFormFields.makeName.trim()
            : null,
          modelName: this.state.deviceFormFields?.modelName
            ? this.state.deviceFormFields.modelName.trim()
            : null,
          serialNumber: this.state.deviceFormFields?.serialNumber
            ? this.state.deviceFormFields.serialNumber.trim()
            : null,
        };
        const deviceId = this.state.selectedDeviceId;
        await updateDevicesByNumberId(
          this.props.match.params.nid,
          deviceId,
          deviceData,
          this._cancelToken
        )
          .then(async (res) => {
            if (
              res &&
              res.data &&
              res.status === AppConfigProps.httpStatusCode.ok &&
              res.data.result
            ) {
              let index = this.state.deviceList.findIndex(
                (item) => item.deviceId === res.data.result.deviceId
              );
              if (index > -1) {
                this.state.deviceList.splice(index, 1, res.data.result);
              }
              await this.setStateAsync({
                deviceList: [...this.state.deviceList],
                isUpdateDeviceDialog: false,
              });
              await this.props.hidePageWaitDialog();
            } else {
              await this.setStateAsync({
                errorDeviceDetails: IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "notification.error.updateDeviceFailed"
                ),
              });
              await this.props.hidePageWaitDialog();
            }
          })
          .catch(async (err) => {
            await this.props.hidePageWaitDialog();
            const errorStatus = ResponseUtil.getStatus(err);
            if (
              errorStatus &&
              errorStatus === AppConfigProps.httpStatusCode.badRequest
            ) {
              const primeError = ResponseUtil.getPrimeError(err);
              if (
                primeError &&
                (primeError.code ===
                  VoiceAdminConstants.SERVER_ERROR_CODE.DEVICE_TYPE_NAME ||
                  primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE
                      .DEVICE_UPDATE_FAILED ||
                  primeError.code ===
                    VoiceAdminConstants.SERVER_ERROR_CODE.DEVICE_TYPE_REQUIRED)
              ) {
                await this.setStateAsync({
                  errorDeviceDetails: primeError.message,
                });
              }
              await this.setStateAsync({
                errorDeviceMessage: IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "notification.error.updateDeviceFailed"
                ),
              });
            } else {
              await manageError(err, this.props.history);
            }
          });
      }
    } else {
      this.setStateAsync({
        errorDeviceMessage: IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.error.deviceNameRequired"
        ),
      });
    }
  };

  renderDeviceAdd = () => {
    return (
      <Dialog
        className="vpa-bn-dialog"
        open={this.state.isAddNewDeviceDialog}
        content={
          <>
            <Text className="page-text text-semibold vpa-dialog-header text-fc-primary">
              {IntlUtil.getText(
                this._intl_ns_vpa_number_inventory,
                "content.addDevice"
              )}
            </Text>
            <Divider size={1} />
            <Grid fluid className="page-grid m-l-0 p-l-0">
              <Row className="m-t-5 m-l-0 p-l-0">
                {this.state.errorDeviceMessage &&
                this.state.errorDeviceMessage.length > 0 ? (
                  <Col>
                    <AlertErrorMessage
                      content={this.state.errorDeviceMessage}
                      moreDetails={{
                        label: IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.moreDetails"
                        ),
                        content: this.state.errorDeviceDetails,
                      }}
                    />
                  </Col>
                ) : null}
                {this.state.successDeviceMessage &&
                this.state.successDeviceMessage.length > 0 ? (
                  <Col>
                    <AlertSuccessMessage
                      content={this.state.successDeviceMessage}
                    />
                  </Col>
                ) : null}
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={6} lg={6} xl={6} className="vpa-bn-text">
                  <div>
                    <FormLabel
                      required
                      className="page-text text-semibold text-fc-dark"
                    >
                      {IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.device_type"
                      )}
                    </FormLabel>
                  </div>
                  <div className="m-t-20">
                    <Dropdown
                      highlightFirstItemOnOpen={isNullOrEmpty(
                        this.state.deviceFormFields.deviceTypeName
                      )}
                      name="deviceTypeName"
                      placeholder="Select Device Type"
                      items={this.state.deviceTypeList}
                      className="vpa-dropdown"
                      renderItem={(Item, props) => {
                        if (props.key === "") {
                          return (
                            <Item
                              {...props}
                              className="vpa-dropdown-none"
                              header={props.header}
                            />
                          );
                        } else {
                          return <Item {...props} header={props.header} />;
                        }
                      }}
                      inverted
                      defaultValue={this.state.deviceFormFields.deviceTypeName}
                      value={this.state.deviceFormFields.deviceTypeName}
                      onChange={(e, select) => {
                        if (
                          this.state.deviceFormFields.deviceTypeName !==
                          select?.value?.header
                        ) {
                          this.setDeviceFormFieldValue(
                            select.name,
                            select.value.header
                          );
                        }
                      }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6} lg={6} xl={6} className="vpa-bn-text">
                  <div>
                    <FormLabel className="page-text text-semibold text-fc-dark">
                      {IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.make_Brand"
                      )}
                    </FormLabel>
                  </div>
                  <div className="m-t-20">
                    <Dropdown
                      highlightFirstItemOnOpen={isNullOrEmpty(
                        this.state.deviceFormFields.makeName
                      )}
                      items={this.state.makeList}
                      id="makeName"
                      name="makeName"
                      placeholder="Select Make"
                      className="vpa-dropdown"
                      renderItem={(Item, props) => {
                        if (props.key === "") {
                          return (
                            <Item
                              {...props}
                              className="vpa-dropdown-none"
                              header={props.header}
                            />
                          );
                        } else {
                          return <Item {...props} header={props.header} />;
                        }
                      }}
                      inverted={true}
                      value={this.state.deviceFormFields.makeName}
                      onChange={(e, select) => {
                        if (
                          this.state.deviceFormFields.makeName !==
                          select?.value?.header
                        ) {
                          this.setDeviceFormFieldValue(
                            select.name,
                            select.value.key
                          );
                        }
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="m-t-10 vpa-bn-row">
                <Col xs={12} md={6} lg={6} xl={6} className="vpa-bn-text">
                  <div>
                    <FormLabel className="page-text text-semibold text-fc-dark">
                      {IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.model"
                      )}
                    </FormLabel>
                  </div>
                  <div className="m-t-20">
                    <Dropdown
                      items={this.state.modelList}
                      id="modelName"
                      name="modelName"
                      placeholder="Select Model"
                      className="vpa-dropdown"
                      highlightFirstItemOnOpen={isNullOrEmpty(
                        this.state.deviceFormFields.modelName
                      )}
                      renderItem={(Item, props) => {
                        if (props.key === "") {
                          return (
                            <Item
                              {...props}
                              className="vpa-dropdown-none"
                              header={props.header}
                            />
                          );
                        } else {
                          return <Item {...props} header={props.header} />;
                        }
                      }}
                      inverted={true}
                      value={this.state.deviceFormFields.modelName}
                      onChange={(e, select) => {
                        if (
                          this.state.deviceFormFields.modelName !==
                          select?.value?.header
                        ) {
                          this.setDeviceFormFieldValue(
                            select.name,
                            select.value.key
                          );
                        }
                      }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6} lg={6} xl={6} className="vpa-bn-text">
                  <div>
                    <FormLabel className="page-text text-semibold text-fc-dark">
                      {IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.serialNumber"
                      )}
                    </FormLabel>
                  </div>
                  <div className="m-t-20">
                    <Input
                      id="serialNumber"
                      name="serialNumber"
                      className="vpa-inventory-input"
                      inverted={true}
                      value={this.state.deviceFormFields.serialNumber}
                      onChange={(e) => {
                        this.setDeviceFormFieldValue(
                          e.target.id,
                          e.target.value
                        );
                      }}
                    />
                  </div>
                </Col>
              </Row>
              <Row className="m-t-20 m-l-0">
                <Col className="m-r-10">
                  <Button
                    onClick={() => this.addDeviceData()}
                    primary
                    className="vpa-save-btn"
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_number_inventory,
                      "content.save"
                    )}
                  />
                </Col>
                <Col className="m-r-10">
                  <Button
                    onClick={() => this.handleHideAddDeviceDialog()}
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_number_inventory,
                      "content.close"
                    )}
                  />
                </Col>
              </Row>
            </Grid>
          </>
        }
      />
    );
  };

  setDeviceFormFieldValue = async (fieldName, fieldValue) => {
    await this.clearAlert();
    if (fieldName && this.state.deviceFormFields) {
      await this.setStateAsync({
        deviceFormFields: {
          ...this.state.deviceFormFields,
          [fieldName]: fieldValue ? fieldValue : "",
        },
      });
    }

    if (this.state.deviceTypeList && this.state.deviceTypeList.length > 0) {
      this.state.deviceTypeList.forEach((devices) => {
        if (devices.header === this.state.deviceFormFields.deviceTypeName) {
          this.setStateAsync({
            deviceFormFields: {
              ...this.state.deviceFormFields,
              deviceTypeId: devices.key,
            },
          });
        }
      });
    }
    if (fieldName === "deviceTypeName") {
      await this.setStateAsync({ makeList: [], modelList: [] });
      let makeList = [];
      if (this.props.inventoryDeviceMakeModelData) {
        this.props.inventoryDeviceMakeModelData.forEach(
          async (deviceTypeData) => {
            if (
              deviceTypeData.deviceTypeName ===
              this.state.deviceFormFields.deviceTypeName
            ) {
              makeList.push({
                key: deviceTypeData.makeName,
                header: deviceTypeData.makeName,
              });
            }
          }
        );
        makeList = [{ key: "", header: "" }].concat(makeList);
        makeList = _.orderBy(
          makeList,
          [(makeName) => makeName.header.toLowerCase()],
          ["asc"]
        );
        makeList = _.uniqBy(makeList, "header");
        await this.setStateAsync({
          makeList: makeList,
        });
        await this.setStateAsync({
          deviceFormFields: {
            ...this.state.deviceFormFields,
            makeName: "",
            modelName: "",
          },
        });
      }
    }
    if (fieldName === "makeName") {
      let modelList = [];
      if (this.props.inventoryDeviceMakeModelData) {
        this.props.inventoryDeviceMakeModelData.forEach(
          async (deviceTypeData) => {
            if (
              deviceTypeData.deviceTypeName ===
                this.state.deviceFormFields.deviceTypeName &&
              deviceTypeData.makeName === fieldValue
            ) {
              modelList.push({
                key: deviceTypeData.modelName,
                header: deviceTypeData.modelName,
              });
            }
          }
        );
        modelList = [{ key: "", header: "" }].concat(modelList);
        modelList = _.orderBy(
          modelList,
          [(modelName) => modelName.header.toLowerCase()],
          ["asc"]
        );
        modelList = _.uniqBy(modelList, "header");
        await this.setStateAsync({
          modelList: modelList,
        });
        await this.setStateAsync({
          deviceFormFields: {
            ...this.state.deviceFormFields,
            modelName: "",
          },
        });
      }
    }
  };

  handleShowAddDeviceDialog = async () => {
    await this.clearAlert();
    await this.setStateAsync({
      isAddNewDeviceDialog: true,
      deviceFormFields: this.deviceFormFields(),
      makeList: [],
      modelList: [],
    });
  };

  handleHideAddDeviceDialog = async () => {
    await this.setStateAsync({
      isAddNewDeviceDialog: false,
      deviceFormFields: this.deviceFormFields(),
      makeList: [],
      modelList: [],
    });
  };

  getNumbersSyncInfoText = (timeStamp) => {
    if (timeStamp) {
      if (
        timeStamp.processStatus !==
        VoiceAdminConstants.INVENTORY.DATA_PROPAGATION
          .ITEM_PROCESS_STATUS_COMPLETED
      ) {
        let text = IntlUtil.getText(
          this._intl_ns_vpa_number_inventory,
          "notification.info.syncInProgress"
        );
        return text;
      } else {
        let syncTimestamp = timeStamp.endTimestamp;
        const timestampObj = moment(syncTimestamp);
        const timeString = timestampObj.fromNow();
        let text = "";
        if (timestampObj && timeString) {
          text = IntlUtil.getSubstituteText(
            this._intl_ns_vpa_number_inventory,
            "notification.info.lastSyncTime",
            [
              {
                key: "<TIME>",
                value: timeString,
              },
            ]
          );
        }
        return text;
      }
    } else return "";
  };

  render() {
    let platformObj = getCommunicationPlatformByName(
      this.props.communicationPlatformsData,
      this.state.editNumberFields?.communicationPlatform
    );
    return (
      <>
        <AppPageTitle
          pageTitle={IntlUtil.getText(
            this._intl_ns_vpa_number_inventory,
            "title.numberEdit"
          )}
        />
        <div ref={this.numberEditPageRef}>
          <Flex column className="page-wrapper">
            <Flex column className="page-title-wrapper">
              <Text className="page-title-text">
                {IntlUtil.getText(
                  this._intl_ns_vpa_number_inventory,
                  "content.editNumber"
                )}
              </Text>
            </Flex>
            <PageLoader
              status={this.state.isPageDataFetched}
              size="medium"
              labelPosition="below"
              label={IntlUtil.getText(
                this._intl_ns_common,
                "content.loadingInprogress"
              )}
              type="full-center"
            >
              <Grid fluid className="p-l-0 m-l-20">
                <Row className="m-t-10 p-l-0 m-l-0 m-b-10">
                  <Col xs={12} lg={12} md={12} xl={12}>
                    {this.state.errorMessage ? (
                      <Row>
                        <Col xs={12} className="m-b-10 m-l-0 p-l-0">
                          <AlertErrorMessage
                            content={this.state.errorMessage}
                            moreDetails={{
                              label: IntlUtil.getText(
                                this._intl_ns_vpa_number_inventory,
                                "content.moreDetails"
                              ),
                              content: this.state.errorDetails,
                            }}
                          />
                        </Col>
                      </Row>
                    ) : null}
                    {this.state.successMessage ? (
                      <Row>
                        <Col xs={12} className="m-b-10 m-l-0 p-l-0">
                          <AlertSuccessMessage
                            content={this.state.successMessage}
                          />
                        </Col>
                      </Row>
                    ) : null}
                  </Col>
                </Row>
                <Row>
                  <Col xl={3} lg={6} md={6} sm={12} xs={12}>
                    <div>
                      <FormLabel
                        required
                        className="page-text vpa-inv-label text-semibold text-fc-dark"
                      >
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.numberCountry"
                        )}
                      </FormLabel>
                    </div>
                    <div className="m-t-10">
                      <Input
                        name="numberCountryName"
                        type="text"
                        disabled={true}
                        className="vpa-inventory-input vpa-inventory-input-d"
                        value={this.state.editNumberFields?.numberCountryName}
                      />
                    </div>
                  </Col>
                  <Col
                    xl={3}
                    lg={6}
                    md={6}
                    sm={12}
                    xs={12}
                    onMouseLeave={() => this.setStateAsync({ isEdited: false })}
                  >
                    <div>
                      <FormLabel
                        required
                        className="page-text vpa-inv-label text-semibold text-fc-dark"
                      >
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.number"
                        )}
                      </FormLabel>
                    </div>
                    <div className="m-t-10">
                      {this.state.isEdited === false ? (
                        <Flex className="vpa-inventory-number-input">
                          <Input
                            name="phoneNumber"
                            maxLength="20"
                            inputRef={this.phoneNumberRef}
                            type="text"
                            inverted
                            disabled={true}
                            className="vpa-inventory-input vpa-input-none"
                            value={`+${this.state.editNumberFields?.phoneNumber}`}
                          />
                          <Button
                            onClick={this.handleNumberEdit}
                            primary
                            text
                            icon={<EditIcon />}
                            iconOnly
                            className="vpa-inv-edit-icon broder-l"
                          />
                        </Flex>
                      ) : (
                        <>
                          <Dropdown
                            open={this.state.isEdited}
                            name="phoneNumber"
                            placeholder="Select Phone Number"
                            search={(filteredItemsByValue, searchQuery) => {
                              const result = filteredItemsByValue.filter(
                                (item) =>
                                  item.toLowerCase() &&
                                  _.startsWith(
                                    item.toLowerCase(),
                                    searchQuery.toLowerCase()
                                  )
                              );
                              if (result.length === 0) {
                                return [];
                              }
                              return result;
                            }}
                            items={this.state.numbersList}
                            className="vpa-dropdown vpa-dropdown__ul"
                            renderItem={(Item, props) => (
                              <Item {...props} header={`+${props.header}`} />
                            )}
                            inverted
                            onChange={this.handleEditNumberPhoneFormFieldChange}
                          />
                        </>
                      )}
                    </div>
                  </Col>
                  <Col xl={3} lg={6} md={6} sm={12} xs={12}>
                    <div>
                      <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.userName"
                        )}
                        {this.state.editNumberFields.manageStatus !== null &&
                        this.state.editNumberFields.manageStatus
                          .toLowerCase()
                          .trim() ===
                          VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED.toLowerCase() &&
                        this.state.editNumberFields.communicationPlatform ===
                          "Microsoft Teams" ? (
                          <Tooltip
                            onOpenChange={(e, data) =>
                              this.setStateAsync({ isShowTooltip: data.open })
                            }
                            open={this.state.isShowTooltip}
                            trigger={
                              <ExclamationCircleIcon className="m-l-5" />
                            }
                            content={
                              <Text>
                                {this.getNumbersSyncInfoText(
                                  this.state.syncRecordsDetails
                                )}
                              </Text>
                            }
                            align="center"
                          />
                        ) : null}
                      </FormLabel>
                    </div>
                    <div className="m-t-10">
                      <Input
                        maxLength="50"
                        type="text"
                        inverted
                        className="vpa-inventory-input vpa-inventory-input-d"
                        name="userName"
                        disabled={
                          this.state.editNumberFields.manageStatus.toLowerCase() ===
                          VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED.toLowerCase()
                            ? true
                            : false
                        }
                        value={this.state.editNumberFields.userName}
                        onChange={(e, select) =>
                          this.setEditFormFieldValue(
                            e.target.name,
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col xl={3} lg={6} md={6} sm={12} xs={12}>
                    <Row>
                      <Col xl={6} lg={4} md={4} sm={3} xs={3}>
                        <div className="m-t-10">
                          <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                            {IntlUtil.getText(
                              this._intl_ns_vpa_number_inventory,
                              "content.managed"
                            )}
                          </FormLabel>
                        </div>
                        <div>
                          {this.state.editNumberFields &&
                          this.state.editNumberFields?.manageStatus.toLowerCase() ===
                            VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED.toLowerCase() ? (
                            <Flex gap="gap.medium" vAlign="center">
                              <RadioGroupItem
                                className="vpa-inv-radio m-t-0"
                                label={IntlUtil.getText(
                                  this._intl_ns_vpa_number_inventory,
                                  "content.yes"
                                )}
                                value={
                                  this.state.editNumberFields?.manageStatus
                                }
                                checked={true}
                              />
                              {platformObj ? (
                                <div>
                                  <Tooltip
                                    content={handleCommunicationPlatformName(
                                      platformObj
                                    )}
                                    trigger={
                                      <Image
                                        className="cpl-image"
                                        src={platformObj?.platformIcon}
                                      />
                                    }
                                  />
                                </div>
                              ) : null}
                            </Flex>
                          ) : (
                            <RadioGroupItem
                              className="vpa-inv-radio"
                              label={IntlUtil.getText(
                                this._intl_ns_vpa_number_inventory,
                                "content.no"
                              )}
                              value={this.state.editNumberFields?.manageStatus}
                              checked={true}
                            />
                          )}
                        </div>
                      </Col>
                      <Col xl={6} lg={4} md={4} sm={3} xs={3}>
                        <div className="m-t-10">
                          <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                            {IntlUtil.getText(
                              this._intl_ns_vpa_number_inventory,
                              "content.classification"
                            )}
                          </FormLabel>
                        </div>
                        <div>
                          <FormLabel
                            className="vpa-inv-label-1"
                            content={
                              this.state.editNumberFields.classification
                                ?.toLowerCase()
                                .trim() ===
                              VoiceAdminConstants.INVENTORY.CLASSIFICATION_TYPE.STORE?.toLowerCase().trim()
                                ? VoiceAdminConstants.INVENTORY
                                    .CLASSIFICATION_TYPE.E_FAX
                                : capitalizeFirstLetter(
                                    this.state.editNumberFields.classification
                                  )
                            }
                          />
                        </div>
                      </Col>
                    </Row>
                  </Col>
                  <Col xl={3} lg={6} md={6} sm={12} xs={12}>
                    <div className="m-t-10">
                      <FormLabel
                        required
                        className="page-text vpa-inv-label text-semibold text-fc-dark"
                      >
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.status"
                        )}
                      </FormLabel>
                    </div>
                    <div>
                      {this.state.editNumberFields.manageStatus
                        .toLowerCase()
                        .trim() ===
                      VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED.toLowerCase() ? (
                        <FormLabel
                          className="vpa-inv-label-1"
                          content={
                            this.state.editNumberFields.assignStatus
                              ? this.state.editNumberFields.assignStatus
                              : "-"
                          }
                        />
                      ) : (
                        <RadioGroup
                          role="radiogroup"
                          items={this.getAssignStatus()}
                          name="assignStatus"
                          className="vpa-inv-radio"
                          checkedValue={
                            this.state.editNumberFields?.assignStatus
                          }
                          onCheckedValueChange={(e, select) =>
                            this.setEditFormFieldValue(
                              "assignStatus",
                              select.value
                            )
                          }
                        />
                      )}
                    </div>
                  </Col>
                  <Col
                    xl={3}
                    lg={6}
                    md={6}
                    sm={12}
                    xs={12}
                    className="inv-row-adjust-b"
                  >
                    <div className="m-t-10">
                      <FormLabel
                        required
                        className="page-text vpa-inv-label  text-semibold text-fc-dark"
                      >
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.reserved"
                        )}
                      </FormLabel>
                    </div>
                    <div>
                      <RadioGroup
                        role="radiogroup"
                        items={this.getReserveStatus()}
                        name="reserveStatus"
                        className="vpa-inv-radio"
                        checkedValue={
                          this.state.editNumberFields?.reserveStatus
                        }
                        onCheckedValueChange={(e, select) =>
                          this.setEditFormFieldValue(
                            "reserveStatus",
                            select.value
                          )
                        }
                      />
                    </div>
                  </Col>
                  <Col
                    xl={3}
                    lg={6}
                    md={6}
                    sm={12}
                    xs={12}
                    className="inv-row-adjust-b"
                  >
                    <div className="m-t-10">
                      <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.connectedToDevice"
                        )}
                      </FormLabel>
                    </div>
                    <div>
                      <RadioGroup
                        role="radiogroup"
                        items={this.getAddDeviceStatus()}
                        name="connectedToDevice"
                        className="vpa-inv-radio"
                        checkedValue={this.state.deviceConnectedStatus}
                        onCheckedValueChange={(e, select) =>
                          this.setEditFormFieldValue(
                            "connectedToDevice",
                            select.value
                          )
                        }
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col xl={3} md={6} lg={6} sm={12} xs={12}>
                    <div className="m-t-10">
                      <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.department"
                        )}
                      </FormLabel>
                    </div>
                    <div className="m-t-10">
                      <Dropdown
                        highlightFirstItemOnOpen={isNullOrEmpty(
                          this.state.editNumberFields.departmentName
                        )}
                        name="departmentId"
                        placeholder="Select Department"
                        items={this.state.departmentsList}
                        className="vpa-dropdown"
                        renderItem={(Item, props) => {
                          if (props.key === "") {
                            return (
                              <Item
                                {...props}
                                className="vpa-dropdown-none"
                                header={""}
                              />
                            );
                          } else {
                            return <Item {...props} header={props.header} />;
                          }
                        }}
                        inverted
                        defaultValue={
                          this.state.editNumberFields?.departmentName
                        }
                        value={this.state.editNumberFields?.departmentName}
                        onChange={(e, select) =>
                          this.setEditFormFieldValue(select.name, select.value)
                        }
                      />
                    </div>
                  </Col>
                  <Col xl={3} md={6} lg={6} sm={12} xs={12}>
                    <div className="m-t-10">
                      <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.carrier"
                        )}
                      </FormLabel>
                    </div>
                    <div className="m-t-10">
                      <Dropdown
                        highlightFirstItemOnOpen={isNullOrEmpty(
                          this.state.editNumberFields.carrierName
                        )}
                        name="carrierId"
                        placeholder="Select Carrier"
                        items={this.state.carriersList}
                        renderItem={(Item, props) => {
                          if (props.key === "") {
                            return (
                              <Item
                                {...props}
                                className="vpa-dropdown-none"
                                header={""}
                              />
                            );
                          } else {
                            return <Item {...props} header={props.header} />;
                          }
                        }}
                        className="vpa-dropdown vpa-dropdown-d"
                        inverted
                        disabled={
                          this.state.editNumberFields.carrierLock
                            ?.toLowerCase()
                            .trim() ===
                          VoiceAdminConstants.INVENTORY.CARRIERLOCK.YES.toLowerCase().trim()
                            ? true
                            : false
                        }
                        defaultValue={this.state.editNumberFields?.carrierName}
                        value={this.state.editNumberFields?.carrierName}
                        onChange={(e, select) =>
                          this.setEditFormFieldValue(select.name, select.value)
                        }
                      />
                    </div>
                  </Col>
                  <Col xl={3} md={6} lg={6} sm={12} xs={12}>
                    <div className="m-t-10">
                      <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.numberType"
                        )}
                      </FormLabel>
                    </div>
                    <div className="m-t-10">
                      <Dropdown
                        highlightFirstItemOnOpen={isNullOrEmpty(
                          this.state.editNumberFields.numberTypeName
                        )}
                        name="numberTypeId"
                        placeholder="Select Number Type"
                        items={this.state.numberTypeList}
                        renderItem={(Item, props) => {
                          if (props.key === "") {
                            return (
                              <Item
                                {...props}
                                className="vpa-dropdown-none"
                                header={""}
                              />
                            );
                          } else {
                            return <Item {...props} header={props.header} />;
                          }
                        }}
                        className="vpa-dropdown"
                        inverted
                        defaultValue={
                          this.state.editNumberFields?.numberTypeName
                        }
                        value={this.state.editNumberFields?.numberTypeName}
                        onChange={(e, select) =>
                          this.setEditFormFieldValue(select.name, select.value)
                        }
                      />
                    </div>
                  </Col>
                  <Col xl={3} md={6} lg={6} sm={12} xs={12}>
                    <div className="m-t-10">
                      <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.notes"
                        )}
                      </FormLabel>
                    </div>
                    <div className="m-t-10">
                      <Input
                        type="text"
                        maxLength="100"
                        name="numberNotes"
                        inverted
                        className="vpa-inventory-input"
                        value={this.state.editNumberFields?.numberNotes}
                        onChange={(e, select) =>
                          this.setEditFormFieldValue(
                            e.target.name,
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col xl={6} md={12} lg={6} sm={12} xs={12}>
                    <Flex className="m-t-20 vpa-number-section">
                      <Text className="vpa-number-section-title-text">
                        {IntlUtil.getText(
                          this._intl_ns_vpa_number_inventory,
                          "content.addressDetails"
                        )}
                      </Text>
                    </Flex>
                    <Flex className="vpa-number-section-content">
                      <div className="vpa-number-section-detail p-15">
                        <Row
                          className={classNames("vpa-inv-row-4", {
                            "vpa-inv-row-5":
                              this.state.editNumberFields.manageStatus.toLowerCase() ===
                              VoiceAdminConstants.INVENTORY.MANAGE_STATUS.MANAGED.toLowerCase(),
                            "":
                              this.state.editNumberFields.manageStatus.toLowerCase() ===
                              VoiceAdminConstants.INVENTORY.MANAGE_STATUS.UNMANAGED.toLowerCase(),
                          })}
                        >
                          {this.state.editNumberFields &&
                          this.state.editNumberFields.manageStatus.toLowerCase() ===
                            VoiceAdminConstants.INVENTORY.MANAGE_STATUS.UNMANAGED.toLowerCase() ? (
                            <>
                              <Col
                                xs={10}
                                sm={10}
                                md={10}
                                lg={10}
                                xl={10}
                                className="m-b-10"
                              >
                                <Dropdown
                                  highlightFirstItemOnOpen={isNullOrEmpty(
                                    this.state.editNumberFields.address
                                  )}
                                  id="address"
                                  name="address"
                                  placeholder="Select Address"
                                  items={this.state.fullAddress}
                                  className="vpa-dropdown"
                                  inverted
                                  renderItem={(Item, props) => {
                                    if (props.key === "") {
                                      return <Item {...props} header="None" />;
                                    } else {
                                      return (
                                        <Item
                                          {...props}
                                          header={props.header}
                                        />
                                      );
                                    }
                                  }}
                                  defaultValue={
                                    this.state.editNumberFields?.address
                                  }
                                  value={this.state.editNumberFields?.address}
                                  onChange={(e, select) =>
                                    this.setEditFormFieldValue(
                                      select.name,
                                      select.value
                                    )
                                  }
                                />
                                {this.state.newAddress === true ? (
                                  <NewSubscriberAddress
                                    showDialogBox={this.state.newAddress}
                                    hideDialogBox={() =>
                                      this.setState({ newAddress: false })
                                    }
                                    fetchSubscriberAddress={() =>
                                      this.fetchSubscriberAddress()
                                    }
                                    history={this.props.history}
                                    countryStatesData={
                                      this.props.countryStatesData
                                    }
                                  />
                                ) : null}
                              </Col>
                              <Col
                                xl={2}
                                md={2}
                                lg={2}
                                sm={2}
                                xs={2}
                                className="vpa-inv-tooltip m-b-10"
                              >
                                <Tooltip
                                  content="Add New Address"
                                  trigger={
                                    <Button
                                      content={"Add"}
                                      primary
                                      className="m-l--10"
                                      iconOnly
                                      text
                                      onClick={() =>
                                        this.setStateAsync({ newAddress: true })
                                      }
                                      icon={<AddIcon />}
                                    />
                                  }
                                />
                              </Col>
                            </>
                          ) : null}
                        </Row>
                        <Row>
                          <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                            <div>
                              <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                                {IntlUtil.getText(
                                  this._intl_ns_vpa_number_inventory,
                                  "content.streetAddress1"
                                )}
                              </FormLabel>
                            </div>
                            <div>
                              <Text as="div" className="text-truncate">
                                <Texty className="page-text">
                                  {this.state.editNumberFields.streetAddress1
                                    ? this.state.editNumberFields.streetAddress1
                                    : "-"}
                                </Texty>
                              </Text>
                            </div>
                          </Col>
                          <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                            <div>
                              <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                                {IntlUtil.getText(
                                  this._intl_ns_vpa_number_inventory,
                                  "content.streetAddress2"
                                )}
                              </FormLabel>
                            </div>
                            <div>
                              <Text as="div" className="text-truncate">
                                <Texty className="page-text">
                                  {this.state.editNumberFields.streetAddress2
                                    ? this.state.editNumberFields.streetAddress2
                                    : "-"}
                                </Texty>
                              </Text>
                            </div>
                          </Col>
                          <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                            <div>
                              <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                                {IntlUtil.getText(
                                  this._intl_ns_vpa_number_inventory,
                                  "content.city"
                                )}
                              </FormLabel>
                            </div>
                            <div>
                              <Text className="text-truncate">
                                <Texty className="page-text">
                                  {this.state.editNumberFields.city
                                    ? this.state.editNumberFields.city
                                    : "-"}
                                </Texty>
                              </Text>
                            </div>
                          </Col>
                        </Row>
                        <Row className="m-b-30 m-t-30">
                          <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                            <div>
                              <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                                {IntlUtil.getText(
                                  this._intl_ns_vpa_number_inventory,
                                  "content.stateOrProvince"
                                )}
                              </FormLabel>
                            </div>
                            <div>
                              <Text className="text-truncate">
                                <Texty className="page-text">
                                  {this.state.editNumberFields.state
                                    ? this.state.editNumberFields.state
                                    : "-"}
                                </Texty>
                              </Text>
                            </div>
                          </Col>
                          <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                            <div>
                              <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                                {IntlUtil.getText(
                                  this._intl_ns_vpa_number_inventory,
                                  "content.country"
                                )}
                              </FormLabel>
                            </div>
                            <div>
                              <Text className="text-truncate">
                                <Texty className="page-text">
                                  {this.state.editNumberFields.country
                                    ? this.state.editNumberFields.country
                                    : "-"}
                                </Texty>
                              </Text>
                            </div>
                          </Col>
                          <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                            <div>
                              <FormLabel className="page-text vpa-inv-label-1 text-semibold text-fc-dark">
                                {IntlUtil.getText(
                                  this._intl_ns_vpa_number_inventory,
                                  "content.zipOrPostal"
                                )}
                              </FormLabel>
                            </div>
                            <div>
                              <Text className="text-truncate">
                                <Texty className="page-text">
                                  {this.state.editNumberFields.postalCode
                                    ? this.state.editNumberFields.postalCode
                                    : "-"}
                                </Texty>
                              </Text>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Flex>
                  </Col>
                  <Col xl={6} md={12} lg={6} sm={12} xs={12}>
                    {this.state.deviceConnectedStatus ===
                    VoiceAdminConstants.INVENTORY.ADD_DEVICE_STATUS.YES ? (
                      <>
                        <Flex className="m-t-20 vpa-number-section">
                          <Text className="vpa-number-section-title-text">
                            {IntlUtil.getText(
                              this._intl_ns_vpa_number_inventory,
                              "content.deviceManagement"
                            )}
                          </Text>
                          <Tooltip
                            content="Add New Device"
                            trigger={
                              <Button
                                className="h-0"
                                size="small"
                                content={"Add"}
                                primary
                                iconOnly
                                text
                                onClick={() => this.handleShowAddDeviceDialog()}
                                icon={<AddIcon />}
                              />
                            }
                          />
                        </Flex>
                        <Flex className="vpa-number-section-content">
                          <div className="vpa-number-section-list">
                            {this.state.deviceList &&
                            this.state.deviceList.length > 0 ? (
                              <Table className="vpa-number-table-wrapper">
                                <Flex className="vpa-number-table-header-wrapper">
                                  <TableRow
                                    header
                                    className="vpa-number-table-header-row"
                                  >
                                    {DeviceTableHeaderColumns(
                                      this._intl_ns_vpa_number_inventory
                                    ).map((column) => {
                                      return (
                                        <TableCell
                                          key={column.key}
                                          className="vpa-number-table-header-cell"
                                        >
                                          {column.content}
                                        </TableCell>
                                      );
                                    })}
                                  </TableRow>
                                </Flex>
                                <div className="vpa-number-table-data-wrapper">
                                  {this.state.deviceList.map((data, index) => {
                                    return (
                                      <TableRow
                                        key={`device-list-${index}`}
                                        className="vpa-number-table-data-row"
                                      >
                                        <TableCell className="vpa-number-table-data-cell">
                                          <Texty className="page-text">
                                            {data.deviceTypeName}
                                          </Texty>
                                        </TableCell>
                                        <TableCell className="vpa-number-table-data-cell">
                                          <Texty className="page-text">
                                            {data.makeName}
                                          </Texty>
                                        </TableCell>
                                        <TableCell className="vpa-number-table-data-cell">
                                          <Texty className="page-text">
                                            {data.modelName}
                                          </Texty>
                                        </TableCell>
                                        <TableCell className="vpa-number-table-data-cell">
                                          <Texty className="page-text">
                                            {data.serialNumber}
                                          </Texty>
                                        </TableCell>
                                        <TableCell className="vpa-number-table-data-cell">
                                          <Flex gap="gap.small">
                                            <Button
                                              size="small"
                                              text
                                              onClick={() =>
                                                this.handleShowDeviceDialog(
                                                  data.deviceId
                                                )
                                              }
                                              primary
                                              iconOnly
                                              icon={<EditIcon size="small" />}
                                            />
                                            <Button
                                              size="small"
                                              text
                                              onClick={() =>
                                                this.handleShowDeleteDialog(
                                                  data
                                                )
                                              }
                                              primary
                                              iconOnly
                                              icon={
                                                <TrashCanIcon size="small" />
                                              }
                                            />
                                          </Flex>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </div>
                              </Table>
                            ) : (
                              <Flex
                                inline
                                gap="gap.large"
                                className="vpa-number-data-none"
                              >
                                <Flex>
                                  <Text className="page-text text-fs-s text-fc-dark">
                                    {IntlUtil.getText(
                                      this._intl_ns_vpa_number_inventory,
                                      "notification.info.noDeviceFound"
                                    )}
                                  </Text>
                                </Flex>
                              </Flex>
                            )}
                          </div>
                        </Flex>
                      </>
                    ) : null}
                  </Col>
                </Row>
                {this.renderDeviceAdd()}
                {this.renderDeviceEdit()}
                <Row>
                  <Col className="m-t-30 m-b-10 m-l-10">
                    <Button
                      content={IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.save"
                      )}
                      primary
                      onClick={this.handleUpdateNumberInventory}
                      className="vpa-usn-button m-r-20 m-b-20"
                    />
                    <Button
                      content={IntlUtil.getText(
                        this._intl_ns_vpa_number_inventory,
                        "content.back"
                      )}
                      onClick={this.handleBack}
                      primary
                      className="vpa-usn-button m-r-20 m-b-20"
                    />
                  </Col>
                </Row>
              </Grid>
              {this.state.numberSelect ? (
                <>{this.renderDeviceDeleteDialog()}</>
              ) : null}
            </PageLoader>
          </Flex>
        </div>
      </>
    );
  }
}
const mapStateToProps = (state) => ({
  inventoryNumbersAllRecords:
    state.inventoryNumberStore.inventoryNumbersAllRecords,
  countryStatesData: state.generalStore.countryStatesData,
  inventoryDeviceMakeModelData:
    state.inventoryNumberStore.inventoryDeviceMakeModelData,
  communicationPlatformsData: state.generalStore.communicationPlatformsData,
});

const mapActionToProps = {
  showPageWaitDialog,
  hidePageWaitDialog,
  setInventoryNumbersPageAllRecords,
  setDeviceMakeModel,
};

export default connect(mapStateToProps, mapActionToProps)(NumberEdit);
